import { FileTypeEnum } from 'api/documents';
import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';
import { CheckIcon, UploadIcon, DocumentIcon, TrashIcon } from 'components/icons/Icons';
import { Sizes } from 'components/forms/NumberSelector';
import { ErrorMessage } from 'components/forms/fragments/FieldSet';
import { FC, useCallback, useState, DragEvent } from 'react';
import { Eye } from 'lucide-react';
import { useDocumentViewerDialog } from './useDocumentViewerDialog';

export interface UploadedFile {
  storedFileId: string;
  fileName: string;
}

interface GenericMultiDocumentUploaderProps {
  documentName: string;
  fileType: FileTypeEnum;
  files: UploadedFile[];
  onUpload: (file: File, fileType: FileTypeEnum) => Promise<{ success: boolean; error?: string }>;
  onRemove?: (storedFileId: string) => Promise<void>;
  disabled?: boolean;
  acceptedFormats?: string;
  className?: string;
  uploadedLabel?: string;
}

/**
 * Justificatif constitué de plusieurs fichiers (ex. deux bilans OU une attestation d'expert-comptable) :
 * la zone d'ajout reste disponible après le premier fichier, chaque fichier transmis est listé
 * avec ses actions (visualiser / supprimer). Un seul fichier suffit à considérer le justificatif transmis.
 */
export const GenericMultiDocumentUploader: FC<GenericMultiDocumentUploaderProps> = ({
  documentName,
  fileType,
  files,
  onUpload,
  onRemove,
  disabled = false,
  acceptedFormats = '.pdf,.jpg,.jpeg,.png,.doc,.docx',
  className = '',
  uploadedLabel = 'Transmis'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isDragOver, setIsDragOver] = useState(false);
  const { openDocumentViewer } = useDocumentViewerDialog();

  const inputId = `file-upload-multi-${fileType}-${documentName.replace(/\s/g, '-')}`;
  const isUploaded = files.length > 0;

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      setIsLoading(true);
      setError(undefined);
      const result = await onUpload(file, fileType);
      if (!result.success) {
        setError(result.error || 'Erreur lors de l\'upload');
      }
    } catch (err) {
      console.error(err);
      setError('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  }, [fileType, onUpload]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    event.target.value = '';
  }, [handleFileUpload]);

  const handleClickUpload = useCallback(() => {
    document.getElementById(inputId)?.click();
  }, [inputId]);

  const handleView = useCallback((file: UploadedFile) => {
    openDocumentViewer({
      title: `${documentName} — ${file.fileName}`,
      storedFileId: file.storedFileId,
      showDefaultActions: true
    });
  }, [documentName, openDocumentViewer]);

  const handleRemove = useCallback(async (storedFileId: string) => {
    if (!onRemove) return;
    try {
      await onRemove(storedFileId);
    } catch (err) {
      console.error(err);
    }
  }, [onRemove]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isLoading) {
      setIsDragOver(true);
    }
  }, [disabled, isLoading]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isLoading) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const acceptedTypes = acceptedFormats.split(',').map(t => t.trim().toLowerCase());
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isValidType = acceptedTypes.some(type =>
      type === fileExtension || file.type.includes(type.replace('.', ''))
    );

    if (isValidType) {
      handleFileUpload(file);
    } else {
      setError(`Format non supporté. Formats acceptés: ${acceptedFormats}`);
    }
  }, [disabled, isLoading, acceptedFormats, handleFileUpload]);

  return (
    <div
      className={`
        border rounded-lg p-4 transition-all duration-200
        ${isDragOver
          ? 'border-primary border-dashed bg-primary/5 border-2'
          : 'border-gray-200 bg-white'
        }
        ${className}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <DocumentIcon className="w-5 h-5 text-gray-500" />
          <div>
            <p className="font-medium text-sm">{documentName}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {isDragOver
                ? 'Déposez le fichier ici'
                : isUploaded
                  ? 'Vous pouvez ajouter d\'autres fichiers'
                  : 'Cliquez ou glissez-déposez — plusieurs fichiers possibles'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {isUploaded && (
            <div className="flex items-center gap-1.5 text-green-600">
              <CheckIcon className="w-4 h-4" />
              <span className="text-xs">{uploadedLabel}</span>
            </div>
          )}
          <input
            type="file"
            id={inputId}
            className="hidden"
            accept={acceptedFormats}
            onChange={handleFileChange}
            disabled={disabled || isLoading}
          />
          {isLoading ? (
            <GenericLoading size={Sizes.sm} loading />
          ) : (
            <Button
              color="primary"
              className="cursor-pointer"
              onClick={handleClickUpload}
              disabled={disabled}
              title={isUploaded ? 'Ajouter un fichier' : 'Transmettre un fichier'}
            >
              <UploadIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {isUploaded && (
        <ul className="mt-3 space-y-1.5 pl-8">
          {files.map(file => (
            <li
              key={file.storedFileId}
              className="flex items-center justify-between gap-3 text-sm text-gray-700"
            >
              <span
                className="truncate hover:text-primary hover:underline cursor-pointer transition-colors"
                onClick={() => handleView(file)}
                title={file.fileName}
              >
                {file.fileName}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  type="button"
                  plain
                  onClick={() => handleView(file)}
                  title="Visualiser le fichier"
                >
                  <Eye className="w-4 h-4 text-gray-500 hover:text-primary" />
                </Button>
                {onRemove && (
                  <Button
                    type="button"
                    plain
                    onClick={() => handleRemove(file.storedFileId)}
                    disabled={disabled}
                    title="Supprimer le fichier"
                  >
                    <TrashIcon className="w-4 h-4 text-gray-500 hover:text-red-500" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};
